import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A wrapper around useSearchParams that ensures URL updates always use
 * the current URL state from window.location.search, not React Router's
 * potentially stale internal state.
 *
 * This fixes the issue where changing siteCode in SiteSelect would be
 * overwritten by other components' setSearchParams calls that used
 * React Router's stale searchParams state.
 */
function useSearchParamsSync() {
  const [searchParams, setSearchParamsOriginal] = useSearchParams();

  const setSearchParamsSync = useCallback((updater, options) => {
    // Always read current URL params from window.location to avoid stale state
    const currentParams = new URLSearchParams(window.location.search);

    if (typeof updater === 'function') {
      // If updater is a function, call it with current params
      const result = updater(currentParams);
      // If the function returns params, use them; otherwise use the mutated currentParams
      const finalParams = result instanceof URLSearchParams ? result : currentParams;
      setSearchParamsOriginal(finalParams, { replace: true, ...options });
    } else if (updater instanceof URLSearchParams) {
      // If updater is already URLSearchParams, use it directly
      setSearchParamsOriginal(updater, { replace: true, ...options });
    } else {
      // If updater is an object, convert to URLSearchParams
      const newParams = new URLSearchParams(updater);
      setSearchParamsOriginal(newParams, { replace: true, ...options });
    }
  }, [setSearchParamsOriginal]);

  return [searchParams, setSearchParamsSync];
}

export default useSearchParamsSync;
